import { useCallback, useMemo, useState } from "react"
import type { LibraryItem } from "@/components/library/library-item"
import { libraryItemPickMedia, librarySourceKey, type LibraryPickMedia } from "@/lib/library-source"
import type { ShotFrameSource } from "@/lib/workspace-api"

/**
 * Один выбранный ассет. Храним ПОЛНЫЙ результат, а не ключ: уйдя из папки, элемента
 * в списке уже нет, и восстановить подпись с превью по ключу будет неоткуда — нижняя
 * панель показывала бы пустые дырки.
 */
export type LibraryPickResult = {
  key: string
  source: ShotFrameSource
  label: string
  thumbUrl: string | null
  /** Полноразмерный файл: у видео это стрим, у картинки — сама картинка. */
  url: string | null
  /** Full original for sketch when url is a display variant. */
  downloadUrl?: string | null
  media: LibraryPickMedia
  durationMs: number | null
}

export function libraryPickResult(item: LibraryItem, source: ShotFrameSource): LibraryPickResult {
  const media = libraryItemPickMedia(item)
  return {
    key: librarySourceKey(source),
    source,
    label: item.label,
    thumbUrl: item.thumbUrl ?? null,
    url: item.asset?.url ?? item.asset?.videoUrl ?? null,
    downloadUrl: item.asset?.downloadUrl ?? null,
    media,
    durationMs: item.asset?.durationMs ?? null,
  }
}

export type LibraryPickSelection = {
  picked: readonly LibraryPickResult[]
  has: (key: string) => boolean
  toggle: (result: LibraryPickResult) => void
  /** Добавить пачку в порядке массива, не выходя за лимит. */
  addMany: (results: readonly LibraryPickResult[]) => void
  remove: (key: string) => void
  clear: () => void
  count: number
  /** Лимит исчерпан: невыбранные плитки гаснут, но снять выбор всё ещё можно. */
  full: boolean
}

/**
 * Набор выбранного — намеренно ничего не знает о Библиотеке: это та часть, которую
 * переиспользуют другие точки множественного выбора.
 *
 * Порядок вставки нагружен смыслом: позиция задаёт слот @reference_N в промпте Холста,
 * поэтому набор — массив, а не Set.
 */
export function useLibraryPickSelection({ limit }: { limit?: number } = {}): LibraryPickSelection {
  const [picked, setPicked] = useState<LibraryPickResult[]>([])

  const has = useCallback((key: string) => picked.some((item) => item.key === key), [picked])

  const toggle = useCallback(
    (result: LibraryPickResult) => {
      setPicked((prev) => {
        const without = prev.filter((item) => item.key !== result.key)
        if (without.length !== prev.length) return without
        // Молча вытеснять самый старый нельзя: в сетке с переходами по папкам
        // исчезновение чужой плитки не видно, и человек не поймёт, что потерял.
        if (limit != null && prev.length >= limit) return prev
        return [...prev, result]
      })
    },
    [limit],
  )

  const addMany = useCallback(
    (results: readonly LibraryPickResult[]) => {
      setPicked((prev) => {
        const seen = new Set(prev.map((item) => item.key))
        const fresh = results.filter((item) => {
          if (seen.has(item.key)) return false
          seen.add(item.key)
          return true
        })
        const room = limit == null ? fresh.length : Math.max(0, limit - prev.length)
        const fitted = fresh.slice(0, room)
        if (fitted.length === 0) return prev
        return [...prev, ...fitted]
      })
    },
    [limit],
  )

  const remove = useCallback((key: string) => {
    setPicked((prev) => prev.filter((item) => item.key !== key))
  }, [])

  const clear = useCallback(() => setPicked([]), [])

  return useMemo(
    () => ({
      picked,
      has,
      toggle,
      addMany,
      remove,
      clear,
      count: picked.length,
      full: limit != null && picked.length >= limit,
    }),
    [picked, has, toggle, addMany, remove, clear, limit],
  )
}
